import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  FlatList,
  ActivityIndicator,
  TouchableOpacity,
  TextInput,
  Modal,
  Alert,
  RefreshControl,
  Linking,
} from 'react-native';
import { useFocusEffect, useRouter } from 'expo-router';
import {
  enterpriseGroupApi,
  EnterpriseGroup,
  EnterpriseGroupMember,
} from '../../../src/api/client';
import { getErrorMessage } from '../../../src/utils/errors';
import { requireConnectivity } from '../../../src/utils/connectivity';
import { useTheme } from '../../../src/theme/ThemeContext';
import { useThemedStyles } from '../../../src/theme/useThemedStyles';
import type { Theme } from '../../../src/theme/types';
import ScreenHeader from '../../../src/components/ScreenHeader';

const createStyles = (theme: Theme) => ({
  container: {
    flex: 1,
    backgroundColor: theme.colors.bgSecondary,
  },
  groupInfo: {
    flexDirection: 'row' as const,
    justifyContent: 'space-between' as const,
    alignItems: 'center' as const,
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: theme.colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  groupName: {
    fontSize: 16,
    fontWeight: '600' as const,
    color: theme.colors.textPrimary,
  },
  seatBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    backgroundColor: theme.colors.primaryBg,
  },
  seatBadgeText: {
    fontSize: 13,
    fontWeight: '600' as const,
    color: theme.colors.primary,
  },
  seatWarning: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    backgroundColor: theme.colors.warningBg,
  },
  seatWarningText: {
    fontSize: 13,
    color: theme.colors.warningText,
    textAlign: 'center' as const,
  },
  searchContainer: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    backgroundColor: theme.colors.background,
  },
  searchInput: {
    backgroundColor: theme.colors.surface,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 15,
    color: theme.colors.textPrimary,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  centered: {
    flex: 1,
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
    paddingHorizontal: 24,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 15,
    color: theme.colors.textMuted,
  },
  emptyText: {
    fontSize: 15,
    color: theme.colors.textMuted,
    textAlign: 'center' as const,
  },
  memberRow: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    backgroundColor: theme.colors.surface,
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  memberInfo: {
    flex: 1,
  },
  memberEmail: {
    fontSize: 15,
    fontWeight: '600' as const,
    color: theme.colors.textPrimary,
    marginBottom: 2,
  },
  memberMeta: {
    fontSize: 13,
    color: theme.colors.textMuted,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
    marginRight: 10,
  },
  statusActive: {
    backgroundColor: theme.colors.successBg,
  },
  statusInvited: {
    backgroundColor: theme.colors.warningBg,
  },
  statusText: {
    fontSize: 11,
    fontWeight: '600' as const,
    textTransform: 'uppercase' as const,
  },
  statusActiveText: {
    color: theme.colors.successText,
  },
  statusInvitedText: {
    color: theme.colors.warningText,
  },
  removeBtn: {
    padding: 8,
  },
  removeBtnText: {
    fontSize: 13,
    color: theme.colors.error,
    fontWeight: '600' as const,
  },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center' as const,
    paddingHorizontal: 24,
  },
  modalContent: {
    backgroundColor: theme.colors.background,
    borderRadius: 16,
    padding: 24,
    maxHeight: '80%' as const,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '700' as const,
    color: theme.colors.textPrimary,
    marginBottom: 8,
  },
  modalSubtitle: {
    fontSize: 14,
    color: theme.colors.textMuted,
    marginBottom: 16,
  },
  modalInput: {
    backgroundColor: theme.colors.surface,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 12,
    fontSize: 15,
    color: theme.colors.textPrimary,
    borderWidth: 1,
    borderColor: theme.colors.border,
    minHeight: 100,
    textAlignVertical: 'top' as const,
  },
  modalActions: {
    flexDirection: 'row' as const,
    justifyContent: 'flex-end' as const,
    marginTop: 16,
    gap: 12,
  },
  modalCancelBtn: {
    paddingHorizontal: 16,
    paddingVertical: 10,
  },
  modalCancelText: {
    fontSize: 15,
    color: theme.colors.textMuted,
  },
  modalSubmitBtn: {
    backgroundColor: theme.colors.primary,
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
  },
  modalSubmitText: {
    fontSize: 15,
    color: '#fff',
    fontWeight: '600' as const,
  },
  resultSection: {
    marginTop: 12,
  },
  resultText: {
    fontSize: 13,
    color: theme.colors.textMuted,
    marginBottom: 4,
  },
  resultError: {
    color: theme.colors.error,
  },
  actionRow: {
    paddingHorizontal: 16,
    paddingVertical: 14,
    backgroundColor: theme.colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  actionRowText: {
    fontSize: 15,
    color: theme.colors.primary,
    fontWeight: '500' as const,
  },
  errorText: {
    fontSize: 15,
    color: theme.colors.error,
    textAlign: 'center' as const,
  },
  seatChipRow: {
    flexDirection: 'row' as const,
    flexWrap: 'wrap' as const,
    gap: 8,
    marginBottom: 16,
  },
  seatChip: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: theme.colors.surface,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  seatChipText: {
    fontSize: 14,
    fontWeight: '600' as const,
    color: theme.colors.primary,
  },
  seatTotalRow: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    justifyContent: 'space-between' as const,
    marginBottom: 16,
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: theme.colors.primaryBg,
    borderRadius: 10,
  },
  seatTotalText: {
    fontSize: 16,
    fontWeight: '700' as const,
    color: theme.colors.primary,
  },
  seatResetBtn: {
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  seatResetText: {
    fontSize: 13,
    color: theme.colors.textMuted,
    fontWeight: '500' as const,
  },
});

export default function EnterpriseGroupScreen() {
  const styles = useThemedStyles(createStyles);
  const { colors } = useTheme();
  const router = useRouter();

  const [group, setGroup] = useState<EnterpriseGroup | null>(null);
  const [members, setMembers] = useState<EnterpriseGroupMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  // Add modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [addResult, setAddResult] = useState<{
    added: number;
    alreadyExists: number;
    errors: Array<{ email: string; reason: string }>;
  } | null>(null);

  // "Request more seats" modal state. Seats are added by sales/admin after an
  // offline (invoiced) purchase — there is no in-app checkout.
  const [showSeatModal, setShowSeatModal] = useState(false);

  const fetchData = useCallback(async (showRefresh = false) => {
    if (showRefresh) setIsRefreshing(true);
    else setIsLoading(true);
    setError(null);

    try {
      const [groupRes, membersRes] = await Promise.all([
        enterpriseGroupApi.get(),
        enterpriseGroupApi.listMembers({ search: search || undefined, limit: 100 }),
      ]);
      setGroup(groupRes.data);
      setMembers(membersRes.data.members);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load enterprise group.'));
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [search]);

  useFocusEffect(
    useCallback(() => {
      fetchData();
    }, [fetchData])
  );

  const handleRemoveMember = (member: EnterpriseGroupMember) => {
    if (!requireConnectivity()) return;
    Alert.alert(
      'Remove Member',
      `Remove ${member.email} from the group? Their subscription will be downgraded to free.`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Remove',
          style: 'destructive',
          onPress: async () => {
            try {
              await enterpriseGroupApi.removeMember(member.id);
              fetchData();
            } catch (err) {
              Alert.alert('Error', getErrorMessage(err, 'Failed to remove member.'));
            }
          },
        },
      ]
    );
  };

  const SEAT_REQUEST_EMAIL = 'contact.cynosure@a3rn.com';

  const handleRequestSeats = async () => {
    const subject = encodeURIComponent('Enterprise seat expansion request');
    const body = encodeURIComponent(
      `Group: ${group?.name ?? group?.id ?? ''}\n` +
        `Current seats: ${group?.max_members ?? ''}\n\n` +
        'We would like to add the following number of seats: ',
    );
    try {
      await Linking.openURL(`mailto:${SEAT_REQUEST_EMAIL}?subject=${subject}&body=${body}`);
    } catch {
      Alert.alert(
        'Contact sales',
        `Email ${SEAT_REQUEST_EMAIL} to add more seats to your plan.`,
      );
    }
    setShowSeatModal(false);
  };

  const handleAddMembers = async () => {
    const emails = emailInput
      .split(/[,\n]/)
      .map((e) => e.trim().toLowerCase())
      .filter((e) => e.length > 0);

    if (emails.length === 0) return;
    if (!requireConnectivity()) return;

    setIsAdding(true);
    setAddResult(null);
    try {
      const res = await enterpriseGroupApi.addMembers(emails);
      setAddResult({
        added: res.data.added.length,
        alreadyExists: res.data.already_exists.length,
        errors: res.data.errors,
      });
      if (res.data.added.length > 0) {
        setEmailInput('');
        fetchData();
      }
    } catch (err) {
      Alert.alert('Error', getErrorMessage(err, 'Failed to add members.'));
    } finally {
      setIsAdding(false);
    }
  };

  const headerView = (
    <ScreenHeader
      title="Enterprise Group"
      left={{
        label: 'Go back',
        icon: 'arrow-back',
        onPress: () => router.back(),
        testID: 'enterprise-group.back-button',
      }}
      right={{
        label: 'Add Members',
        text: '+ Add',
        tone: 'accent',
        onPress: () => {
          setAddResult(null);
          setShowAddModal(true);
        },
        testID: 'enterprise-group.add-members-button',
      }}
    />  );

  if (isLoading) {
    return (
      <View style={styles.container}>
        {headerView}
        <View style={styles.centered}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.loadingText}>Loading group...</Text>
        </View>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.container}>
        {headerView}
        <View style={styles.centered}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      </View>
    );
  }

  const atCapacity = group ? group.member_count >= group.max_members : false;

  const renderMember = ({ item }: { item: EnterpriseGroupMember }) => (
    <View
      style={styles.memberRow}
      accessibilityLabel={`${item.email}, ${item.status}`}
      accessibilityRole="button"
      testID={`enterprise-group.member-${item.email}`}
    >
      <View
        style={[
          styles.statusBadge,
          item.status === 'active' ? styles.statusActive : styles.statusInvited,
        ]}
        accessible={false}
      >
        <Text
          style={[
            styles.statusText,
            item.status === 'active' ? styles.statusActiveText : styles.statusInvitedText,
          ]}
          accessible={false}
        >
          {item.status}
        </Text>
      </View>
      <View style={styles.memberInfo} accessible={false}>
        <Text style={styles.memberEmail} accessible={false}>{item.email}</Text>
        {item.display_name && (
          <Text style={styles.memberMeta} accessible={false}>{item.display_name}</Text>
        )}
      </View>
      <TouchableOpacity
        onPress={() => handleRemoveMember(item)}
        style={styles.removeBtn}
        accessibilityLabel={`Remove ${item.email}`}
        accessibilityRole="button"
        testID="enterprise-group.remove-member-button"
      >
        <Text style={styles.removeBtnText} accessible={false}>Remove</Text>
      </TouchableOpacity>
    </View>
  );

  return (
    <View style={styles.container}>
      {headerView}

      {/* Group info bar */}
      {group && (
        <View
          style={styles.groupInfo}
          accessibilityLabel={`${group.name || 'Enterprise Group'}, ${group.member_count} of ${group.max_members} members`}
          testID="enterprise-group.group-info"
        >
          <Text style={styles.groupName} accessible={false}>
            {group.name || 'Enterprise Group'}
          </Text>
          <View style={styles.seatBadge} accessible={false}>
            <Text style={styles.seatBadgeText} accessible={false}>
              {group.member_count} / {group.max_members} seats
            </Text>
          </View>
        </View>
      )}

      {/* Seat limit warning */}
      {atCapacity && (
        <View style={styles.seatWarning} testID="enterprise-group.seat-limit-warning">
          <Text style={styles.seatWarningText} accessible={false}>
            All seats filled. Request more seats to invite more members.
          </Text>
          <TouchableOpacity
            onPress={() => setShowSeatModal(true)}
            accessibilityLabel="Request More Seats"
            accessibilityRole="button"
            testID="enterprise-group.add-seats-button"
          >
            <Text style={[styles.seatWarningText, { fontWeight: '700', marginTop: 4 }]} accessible={false}>
              Request More Seats
            </Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Search */}
      <View style={styles.searchContainer}>
        <TextInput
          style={styles.searchInput}
          placeholder="Search members by email..."
          placeholderTextColor={colors.textMuted}
          value={search}
          onChangeText={setSearch}
          autoCapitalize="none"
          autoCorrect={false}
          keyboardType="email-address"
          accessibilityLabel="Search members"
          testID="enterprise-group.search-input"
        />
      </View>

      {/* Member list */}
      <FlatList
        data={members}
        keyExtractor={(item) => item.id}
        renderItem={renderMember}
        refreshControl={
          <RefreshControl refreshing={isRefreshing} onRefresh={() => fetchData(true)} />
        }
        ListEmptyComponent={
          <View style={styles.centered}>
            <Text style={styles.emptyText}>
              {search
                ? 'No members match your search.'
                : 'No members yet. Add team members by email address.'}
            </Text>
          </View>
        }
        testID="enterprise-group.member-list"
      />

      {/* Add Members Modal */}
      <Modal
        visible={showAddModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowAddModal(false)}
      >
        <View style={styles.modalOverlay} testID="enterprise-group.add-modal">
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Add Members</Text>
            <Text style={styles.modalSubtitle}>
              Enter email addresses separated by commas or new lines.
            </Text>
            <TextInput
              style={styles.modalInput}
              placeholder="user@company.com, another@company.com"
              placeholderTextColor={colors.textMuted}
              value={emailInput}
              onChangeText={setEmailInput}
              multiline
              autoCapitalize="none"
              autoCorrect={false}
              keyboardType="email-address"
              accessibilityLabel="Email addresses to add"
              testID="enterprise-group.email-input"
            />

            {addResult && (
              <View style={styles.resultSection}>
                {addResult.added > 0 && (
                  <Text style={styles.resultText}>
                    {addResult.added} member(s) added successfully.
                  </Text>
                )}
                {addResult.alreadyExists > 0 && (
                  <Text style={styles.resultText}>
                    {addResult.alreadyExists} already in group.
                  </Text>
                )}
                {addResult.errors.map((e, i) => (
                  <Text key={i} style={[styles.resultText, styles.resultError]}>
                    {e.email}: {e.reason}
                  </Text>
                ))}
              </View>
            )}

            <View style={styles.modalActions}>
              <TouchableOpacity
                onPress={() => {
                  setShowAddModal(false);
                  setEmailInput('');
                  setAddResult(null);
                }}
                style={styles.modalCancelBtn}
                accessibilityLabel="Cancel"
                accessibilityRole="button"
              >
                <Text style={styles.modalCancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={handleAddMembers}
                style={[styles.modalSubmitBtn, isAdding && { opacity: 0.6 }]}
                disabled={isAdding || emailInput.trim().length === 0}
                accessibilityLabel="Add members"
                accessibilityRole="button"
                testID="enterprise-group.submit-add-button"
              >
                {isAdding ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <Text style={styles.modalSubmitText}>Add</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Request More Seats Modal */}
      <Modal
        visible={showSeatModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowSeatModal(false)}
      >
        <View style={styles.modalOverlay} testID="enterprise-group.seat-modal">
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Request More Seats</Text>
            <Text style={styles.modalSubtitle}>
              Enterprise plans are invoiced directly. Contact sales to add seats
              and we'll update your plan and send an invoice.
            </Text>

            <View style={styles.modalActions}>
              <TouchableOpacity
                onPress={() => setShowSeatModal(false)}
                style={styles.modalCancelBtn}
                accessibilityLabel="Cancel"
                accessibilityRole="button"
              >
                <Text style={styles.modalCancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={handleRequestSeats}
                style={styles.modalSubmitBtn}
                accessibilityLabel="Contact sales to add seats"
                accessibilityRole="button"
                testID="enterprise-group.confirm-expand-button"
              >
                <Text style={styles.modalSubmitText}>Contact sales</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}
